import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';

// component
import SvgButtonLoader from '../SvgButtonLoader/SvgButtonLoader';

// styles

import {
  CardElementWrapper,
  StyledAddNewCardContainer,
  StyledButtonWithLoading,
  StyledCardInputPlaceholder,
} from './AddNewCard.styled';

// utils
import { attachNewPaymentMethodToCustomer } from './utils';
import { loadSavedCreditCards } from '../../redux/savedCreditCards/action';

// types
import { UserType } from '../../pages/types';
import Keyboard from '../../api/keyboard';
import { setActiveTab, setIsActive, Tabs } from '../../redux/tabBar/reducer';
import { FormattedMessage, useIntl } from 'react-intl';
import { useOptions } from '../StripeCard';
import useToast from '../../context/ToastContext/ToastContext';

interface IAddNewCard {
  user: UserType;
}

const AddNewCard: React.FC<IAddNewCard> = ({ user }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { showToast } = useToast();

  const stripe = useStripe();
  const elements = useElements();
  const options = useOptions();
  const cardInputRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    Keyboard?.addListener('keyboardDidShow', () => {
      if (document.activeElement?.tagName === 'IFRAME') cardInputRef.current?.scrollIntoView();
    });

    Keyboard?.addListener('keyboardWillShow', () => {
      dispatch(setIsActive(false));
    });

    Keyboard?.addListener('keyboardWillHide', () => {
      dispatch(setIsActive(true));
      dispatch(setActiveTab(Tabs.third));
    });

    return () => {
      Keyboard?.removeAllListeners();
    };
  }, []);

  const [isLoading, setIsLoading] = useState(false);

  const addNewCardHandler = async () => {
    setIsLoading(true);
    try {
      const cardElements = elements?.getElement(CardElement);

      if (!stripe || !cardElements) {
        setIsLoading(false);

        showToast({
          text: intl.formatMessage({
            id: 'booking.error.stripe_not_loaded_yet',
            defaultMessage:
              'Сервіс для оплати ще не загрузився, спробуйте сплатити через 10 секунд, або перезагрузіть сторінку.',
          }),
        });
        return;
      }

      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElements,
      });

      if (error) {
        setIsLoading(false);
        showToast({ error });
        return;
      }
      await attachNewPaymentMethodToCustomer({
        paymentMethod,
        user_email: user.email,
        user_name: user.display_name,
        user_id: user.id!,
        intl,
      });

      user?.id && (await dispatch(loadSavedCreditCards(user?.id)));

      cardElements.clear();
      showToast({
        text: 'Картку успішно додано',
      });
    } catch (error) {
      showToast({ error });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <StyledAddNewCardContainer ref={cardInputRef}>
      <StyledCardInputPlaceholder>
        <FormattedMessage id={'cards.add.new.btn'} defaultMessage={'Додати нову карту'} />
      </StyledCardInputPlaceholder>
      <CardElementWrapper>
        <CardElement options={options} />
      </CardElementWrapper>
      <StyledButtonWithLoading disabled={isLoading} onClick={async () => addNewCardHandler()}>
        {isLoading ? (
          <SvgButtonLoader />
        ) : (
          <span>
            <FormattedMessage id={'cards.add.new.btn'} defaultMessage={'Додати нову карту'} />
          </span>
        )}
      </StyledButtonWithLoading>
    </StyledAddNewCardContainer>
  );
};

export default AddNewCard;
